import { useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '../components/button';
import { Dialog } from '../components/dialog';
import { Field } from '../components/field';

import { AuthErrorSummary, describeUnknownError } from './error-summary';
import { PasskeySignInButton, type PasskeySignInButtonProps } from './passkey-buttons';
import { TotpCodeInput } from './totp-code-input';

export interface StepUpDialogProps {
  open: boolean;
  /** Cancel. The original action does not run. */
  onClose: () => void;
  /** `POST /auth/two-factor/verify-totp` with `{ code }`. */
  onVerifyTotp: (code: string) => Promise<void>;
  /**
   * Runs after a factor verifies: the call that answered `403 MFA_STEP_UP_REQUIRED`,
   * retried. A rejection is shown inside the dialog, so a retry that fails for a second
   * reason does not close it and leave the person guessing.
   */
  onVerified: () => Promise<void> | void;
  /** Omit to offer TOTP only, e.g. where no passkey is registered. */
  passkey?: Pick<PasskeySignInButtonProps, 'getOptions' | 'verify'>;
  /** What the person was trying to do, e.g. "start a support session". */
  action?: ReactNode;
  describeError?: (error: unknown) => string;
}

/**
 * The step-up dialog (M2.8, API.md §1.3, SECURITY.md §4.3).
 *
 * `@StepUp()` routes need a second factor verified within ten minutes; the API answers
 * `403 MFA_STEP_UP_REQUIRED` and names the methods it will accept. This is the one place
 * that turns that refusal into a question and then **retries the original call**, which
 * is the part that matters: a step-up that verifies and then drops the action makes the
 * person start over and is how a confirmation dialog gets clicked through twice.
 *
 * Cancelling is real. It closes the dialog and does not run the action, because the
 * alternative - a dialog that cannot be dismissed - would trap someone whose phone is on
 * the other desk.
 *
 * No backup code here, on purpose. Backup codes are single-use account recovery at
 * sign-in; spending one to suspend a tenant is the wrong trade, and the API's own
 * `verify-backup-code` route expects the two-factor challenge cookie, not a session.
 */
export function StepUpDialog(props: StepUpDialogProps) {
  // Remount on open, so a code typed for a cancelled action is never pre-filled for the
  // next one. The same reason `ReasonDialog` in apps/super does it.
  if (!props.open) return null;
  return <StepUpDialogBody {...props} />;
}

function StepUpDialogBody({
  onClose,
  onVerifyTotp,
  onVerified,
  passkey,
  action,
  describeError = describeUnknownError,
}: StepUpDialogProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const finish = async (verify: () => Promise<void>): Promise<void> => {
    setError(null);
    setPending(true);
    try {
      await verify();
      await onVerified();
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setPending(false);
    }
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void finish(() => onVerifyTotp(code));
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      dismissOnBackdrop={false}
      title="Confirm it is you"
      description={
        action === undefined ? 'Enter a code from your authenticator app to continue.' : undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="step-up-form"
            loading={pending}
            loadingLabel="Checking your code"
            disabled={code.length < 6}
          >
            Confirm
          </Button>
        </>
      }
    >
      <form id="step-up-form" onSubmit={submit} noValidate className="flex flex-col gap-4">
        {action === undefined ? null : (
          <p className="text-base leading-[1.5] text-neutral-500">
            Enter a code from your authenticator app to {action}.
          </p>
        )}

        <AuthErrorSummary message={error} />

        <Field label="6-digit code" required>
          <TotpCodeInput name="code" value={code} onValueChange={setCode} autoFocus />
        </Field>

        {passkey === undefined ? null : (
          <PasskeySignInButton
            getOptions={passkey.getOptions}
            verify={passkey.verify}
            onVerified={() => {
              void finish(async () => undefined);
            }}
            onError={setError}
            disabled={pending}
            block
          >
            Use a passkey instead
          </PasskeySignInButton>
        )}
      </form>
    </Dialog>
  );
}
